package com.cs.ejobin.ui.screens.paywall

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.outlined.Workspaces
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.Subscription
import com.cs.ejobin.ui.components.ErrorText
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.PaywallViewModel

@Composable
fun PaywallScreen(
    vm: PaywallViewModel,
    onSuccess: () -> Unit,
    onSkip: () -> Unit
) {
    val state by vm.state.collectAsState()

    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    val plans = remember { Subscription.Plan.entries.toList() }
    var selected by remember { mutableStateOf(plans.firstOrNull()) }

    ScreenScaffold(
        title = t(Keys.paywall_title),
        scrollable = true,
        topActions = {
            TextButton(
                onClick = onSkip,
                enabled = !state.isLoading,
                colors = primaryTextButtonColors
            ) { Text(t(Keys.paywall_not_now)) }
        }
    ) {
        // ✅ Paiement désactivé : message clair
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = AppSurfaces.glassCard(),
            border = CardDefaults.outlinedCardBorder()
        ) {
            Column(
                modifier = Modifier.padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                Text(
                    text = "t(Keys.paywall_disabled_title)",
                    style = MaterialTheme.typography.titleMedium,
                    color = AppColors.TextPrimary
                )
                Text(
                    text = "t(Keys.paywall_disabled_body)",
                    style = MaterialTheme.typography.bodyMedium,
                    color = AppColors.TextPrimary
                )
            }
        }

        PaywallHeader()
        PaywallFeatures()

        Spacer(Modifier.height(4.dp))

        Text(
            text = t(Keys.paywall_choose_plan),
            style = MaterialTheme.typography.titleSmall,
            color = AppColors.TextPrimary
        )

        // ✅ On laisse l’UI mais on désactive l’interaction
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            plans.forEach { plan ->
                PlanCard(
                    title = planTitle(plan),
                    subtitle = planHint(plan),
                    selected = selected == plan,
                    enabled = false, // ✅ désactivé
                    onSelect = { selected = plan }
                )
            }
        }

        ErrorText(state.errorKey)

        // ✅ Remplace “payer” par “continuer” → on fait juste "skip"
        Button(
            onClick = onSkip,
            enabled = !state.isLoading,
            modifier = Modifier.fillMaxWidth(),
            colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
        ) {
            if (state.isLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(18.dp),
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.onPrimary
                )
                Spacer(Modifier.width(10.dp))
                Text(t(Keys.loading))
            } else {
                Text(t(Keys.paywall_not_now))
            }
        }

        Text(
            text = t(Keys.paywall_disclaimer),
            style = MaterialTheme.typography.bodySmall,
            color = AppColors.TextPrimary
        )
    }
}

@Composable
private fun PaywallHeader() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Icon(
                imageVector = Icons.Outlined.Workspaces,
                contentDescription = null,
                tint = AppColors.Primary
            )
            Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    t(Keys.paywall_headline),
                    style = MaterialTheme.typography.titleLarge,
                    color = AppColors.TextPrimary
                )
                Text(
                    t(Keys.paywall_subtitle),
                    style = MaterialTheme.typography.bodyMedium,
                    color = AppColors.TextPrimary
                )
            }
        }
    }
}

@Composable
private fun PaywallFeatures() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = CardDefaults.outlinedCardBorder()
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text(
                t(Keys.paywall_features_title),
                style = MaterialTheme.typography.titleMedium,
                color = AppColors.TextPrimary
            )
            FeatureRow(t(Keys.paywall_feature_1))
            FeatureRow(t(Keys.paywall_feature_2))
            FeatureRow(t(Keys.paywall_feature_3))
        }
    }
}

@Composable
private fun FeatureRow(text: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Icon(
            imageVector = Icons.Outlined.CheckCircle,
            contentDescription = null,
            tint = AppColors.Primary
        )
        Text(
            text,
            style = MaterialTheme.typography.bodyMedium,
            color = AppColors.TextPrimary
        )
    }
}

@Composable
private fun PlanCard(
    title: String,
    subtitle: String,
    selected: Boolean,
    enabled: Boolean,
    onSelect: () -> Unit
) {
    val border = if (selected) BorderStroke(1.dp, AppColors.Primary) else null

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = AppSurfaces.glassCard(),
        border = border,
        onClick = onSelect,
        enabled = enabled
    ) {
        Column(
            modifier = Modifier.padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    title,
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.weight(1f),
                    color = AppColors.TextPrimary
                )
                RadioButton(
                    selected = selected,
                    onClick = onSelect,
                    enabled = enabled,
                    colors = RadioButtonDefaults.colors(selectedColor = AppColors.Primary)
                )
            }
            Text(
                subtitle,
                style = MaterialTheme.typography.bodySmall,
                color = AppColors.TextPrimary
            )
        }
    }
}

@Composable
private fun planTitle(plan: Subscription.Plan): String =
    when (plan) {
        Subscription.Plan.MonthlyTrialThen990 -> t(Keys.paywall_plan_monthly)
        Subscription.Plan.Yearly9990 -> t(Keys.paywall_plan_yearly)
    }

@Composable
private fun planHint(plan: Subscription.Plan): String =
    when (plan) {
        Subscription.Plan.MonthlyTrialThen990 -> t(Keys.paywall_plan_monthly_hint)
        Subscription.Plan.Yearly9990 -> t(Keys.paywall_plan_yearly_hint)
    }